import type { ApiAvailableChain } from 'src/types/chain';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { REFERENCE_STALE_TIME } from './staleness';

/**
 * `GET /buyer/chains` — the networks this client may pay on (multichain spec §4.2).
 *
 * Unauthenticated: it carries no user data, and the storefront needs it before a wallet is
 * connected. The region gate runs on the server from the request IP, so the answer already
 * excludes what the caller's region may not use — the client must never filter chains itself.
 *
 * Cached for the session: the set changes with a deploy, not with a page view.
 *
 * `enabled` lets a screen that needs the registry only for a signed-in buyer (the escrow offer on a
 * listing) skip the request for an anonymous visitor — one request per screen, as batch 7 left it.
 */
export const useBuyerChains = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: QueryKeys.buyerChains(),
    queryFn: async (): Promise<ApiAvailableChain[]> => {
      const response = await axios.get<ApiAvailableChain[]>('/buyer/chains');

      return response.data;
    },
    staleTime: REFERENCE_STALE_TIME,
  });
